import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Printer } from 'lucide-react';
import type { LibraryDocument } from '@worki/shared';
import { api, authedFetch } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { Button } from '../../components/ui.js';
import { Modal } from '../../components/Modal.js';
import { PdfPreview } from '../../components/PdfPreview.js';

/**
 * LIB — a filed document, opened where it was tapped.
 *
 * A row used to hand its file to a new tab, and that is the one thing a phone
 * cannot be relied on to do: a pop-up blocker returns null and the tap does
 * nothing, the browser inside WhatsApp or Gmail opens no tab at all, and
 * Android's download manager fetches the address without the session cookie
 * and is told 401. Every one of those was reported as "clicking the list does
 * nothing". So the document is drawn here, in the same dialog an invoice
 * opens in, with the bytes fetched through the session like everything else.
 */

export type PreviewKind = 'pdf' | 'image' | 'none';

/** What the dialog can draw, decided by the type the server stored. */
export function previewKind(mime: string): PreviewKind {
  if (mime === 'application/pdf' || mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  return 'none';
}

const filePath = (row: LibraryDocument) => `/library/${row.id}/file`;

export function openLibraryForPrint(row: LibraryDocument): Promise<void> {
  const word = row.mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return api.openFile(word ? `/library/${row.id}/preview.pdf` : filePath(row), word ? `${row.title}.pdf` : row.fileName);
}

/*
 * The same box `PdfPreview` draws into, so the dialog keeps one size whether
 * the document is a PDF, a photo, or something it cannot show.
 */
const FRAME = 'w-full h-[76vh] sm:h-[78vh] min-h-[16rem] rounded-xl border border-border bg-muted/60';

export function LibraryPreviewBody({
  document,
  onDownload,
}: {
  document: LibraryDocument;
  onDownload: () => void;
}) {
  const kind = previewKind(document.mime);

  if (kind === 'pdf') {
    // The bare API path: `authedFetch` prefixes the API origin itself, and it
    // is the one place that knows whether the session is a cookie or a header.
    return <PdfPreview src={document.mime==='application/pdf'?filePath(document):`/library/${document.id}/preview.pdf`} fallbackPath={filePath(document)} fallbackName={document.fileName} expanded />;
  }
  if (kind === 'image') return <ImagePreview document={document} onDownload={onDownload} />;
  return <CannotShow onDownload={onDownload} />;
}

/**
 * A photographed permit, a scanned page kept as a JPEG. Fetched rather than
 * pointed at with a plain `src`: the packaged app carries its session as a
 * bearer header, which an `<img>` request never sends.
 */
function ImagePreview({ document, onDownload }: { document: LibraryDocument; onDownload: () => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);

    authedFetch(filePath(document))
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        const blob = await response.blob();
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });

    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [document]);

  if (failed) return <CannotShow onDownload={onDownload} />;

  return (
    <div className={cn(FRAME, 'overflow-auto overscroll-contain p-2 flex items-center justify-center')}>
      {url ? (
        <img src={url} alt={document.title} className="max-w-full max-h-full object-contain rounded shadow-sm" />
      ) : (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      )}
    </div>
  );
}

/** A Word file, or a fetch that failed: say so, and offer the file itself. */
function CannotShow({ onDownload }: { onDownload: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={cn(FRAME, 'flex flex-col items-center justify-center gap-2 text-center px-4')}>
      <FileText className="h-6 w-6 text-muted-foreground/60" />
      <p className="text-sm text-muted-foreground">{t('common.documentFailed')}</p>
      <button type="button" onClick={onDownload} className="text-sm font-medium text-primary hover:underline">
        {t('common.download')}
      </button>
    </div>
  );
}

export function LibraryPreview({
  document,
  onClose,
  onDownload,
  onPrint,
}: {
  document: LibraryDocument | null;
  onClose: () => void;
  onDownload: (row: LibraryDocument) => void;
  onPrint?: (row: LibraryDocument) => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={Boolean(document)}
      title={document?.title ?? ''}
      onClose={onClose}
      size="widest"
      footer={
        document && (
          <>{onPrint && <Button type="button" variant="secondary" icon={<Printer className="h-4 w-4" />} onClick={() => onPrint(document)}>{t('common.print')}</Button>}<Button
            type="button"
            variant="secondary"
            icon={<Download className="h-4 w-4" aria-hidden />}
            onClick={() => onDownload(document)}
          >
            {t('common.download')}
          </Button></>
        )
      }
    >
      {document && <LibraryPreviewBody document={document} onDownload={() => onDownload(document)} />}
    </Modal>
  );
}
